/**
 * The one place the browser sends an error report from. Used by the global
 * listener (ErrorReporter), the route error boundary (app/error.tsx) and the
 * root error boundary (app/global-error.tsx), which previously each built their
 * own payload and none sent any context about where the error happened.
 */
import { getBreadcrumbs } from "./breadcrumbs"
import { parseStack, isExtensionFrame } from "./stack"

export type ClientErrorPayload = {
  message: string
  stack?: string
  /** React component stack, when an error boundary provides one. */
  componentStack?: string
  /** Next.js digest for errors thrown during server rendering. */
  digest?: string
}

/** origin + pathname only: query strings and hashes can carry tokens or PII. */
function safePageUrl(): string {
  try { return window.location.origin + window.location.pathname } catch { return "" }
}

/**
 * True when every frame of the stack is inside a browser extension. Those are
 * not our bugs (on inspection they were 80% of all stored reports, one
 * visitor's extension firing every minute), so they are not sent at all.
 */
export function isExtensionOnly(stack?: string): boolean {
  const frames = parseStack(stack)
  return frames.length > 0 && frames.every(isExtensionFrame)
}

export async function sendClientError(p: ClientErrorPayload): Promise<void> {
  if (!p.message || p.message === "Script error.") return
  if (isExtensionOnly(p.stack)) return
  try {
    await fetch("/api/errors/report", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      keepalive: true, // still delivered if the error leads to a navigation
      // Identity is derived server-side from the session cookie; never sent.
      body: JSON.stringify({
        message: p.message.slice(0, 500),
        stack: p.stack?.slice(0, 5000),
        componentStack: p.componentStack?.slice(0, 2000),
        digest: p.digest?.slice(0, 100),
        url: safePageUrl(),
        userAgent: navigator.userAgent.slice(0, 500),
        breadcrumbs: getBreadcrumbs(),
      }),
    })
  } catch { /* a reporter must never throw */ }
}
